import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Check, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Crop, Loader2, MousePointer2, Eraser, MousePointer, RotateCw, Maximize, RectangleHorizontal, SquareDashed } from 'lucide-react';
import { Button } from './Button';
import {
  A4_QUER,
  type Drehung,
  type Flaeche,
  type Punkt,
  type Rahmen,
  einpassSkala,
  exportSkala,
  naechsteDrehung,
  pdfDrehung,
  planNameAusDatei,
  planNameMitSeite,
  radierungenDrehen90,
  rahmenMitVerhaeltnis,
} from './plaene/planFormat';

// Worker fuer pdf.js: kommt ueber den Vite-?url-Import aus dem Bundle und NICHT
// von unpkg. Die CDN-Variante war auf 4.0.379 verdrahtet, waehrend package.json
// `^4.0.379` erlaubt — nach einem Update bricht pdf.js mit "API version does not
// match Worker version" ab (und offline ohnehin).
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl as string;

interface Props {
  file: File;
  /**
   * Fertiger Plan: JPEG-dataURL (neu in hoher Aufloesung gerendert) und der
   * Planname (getrimmt, nie leer — ohne Namen ist „Übernehmen“ gesperrt).
   */
  onCrop: (imageBase64: string, name: string) => void;
  onCancel: () => void;
  /**
   * Planname ohne Seitenzusatz; Standard aus dem Dateinamen. Bei mehrseitigen
   * PDFs haengt „– S. n“ an und folgt dem Blaettern, bis der Nutzer den Namen
   * selbst aendert.
   */
  startName?: string;
}

type Tool = 'select' | 'erase';
/** A4 quer = Seitenverhaeltnis √2, fuellt die A4-quer-Berichtsseite genau. */
type RahmenArt = 'a4' | 'frei';

/** Breite des Radierers in unskalierten PDF-Einheiten (Massstab 1). */
const RADIERER_BREITE = 15;
/** Zoomstufen der Anzeige. */
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
const ZOOM_SCHRITT = 0.25;
/** JPEG-Qualitaet des Exports (Plaene: Linien und Schrift, wenig Flaeche). */
const EXPORT_QUALITAET = 0.85;

/**
 * Radierungen zeichnen. Pfade liegen in unskalierten Viewport-Koordinaten der
 * aktuellen Drehung; `faktor` = Anzeige-Massstab (Bildschirm) oder 1, wenn der
 * Kontext schon per setTransform skaliert ist (Export). Ein einzelner Tipp
 * ist ein Punkt — frueher fiel er beim Neuzeichnen weg.
 */
function zeichneRadierungen(ctx: CanvasRenderingContext2D, pfade: ReadonlyArray<ReadonlyArray<Punkt>>, faktor: number) {
  if (pfade.length === 0) return;
  ctx.save();
  ctx.strokeStyle = '#ffffff';
  ctx.fillStyle = '#ffffff';
  ctx.lineWidth = RADIERER_BREITE * faktor;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const pfad of pfade) {
    if (pfad.length === 0) continue;
    if (pfad.length === 1) {
      ctx.beginPath();
      ctx.arc(pfad[0].x * faktor, pfad[0].y * faktor, (RADIERER_BREITE * faktor) / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(pfad[0].x * faktor, pfad[0].y * faktor);
    for (let i = 1; i < pfad.length; i++) ctx.lineTo(pfad[i].x * faktor, pfad[i].y * faktor);
    ctx.stroke();
  }
  ctx.restore();
}

export const PdfCropper: React.FC<Props> = ({ file, onCrop, onCancel, startName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null); // Store current render task
  /** Zaehler gegen ueberholte Renderlaeufe (getPage ist asynchron). */
  const renderLaufRef = useRef(0);
  /** Seitengroesse bei Massstab 1 OHNE eigene Drehung (nur Eigendrehung der Seite). */
  const grundMasseRef = useRef<Flaeche | null>(null);
  /** Beim naechsten Rendern die Seite in den sichtbaren Bereich einpassen. */
  const einpassenRef = useRef(true);

  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  /** Canvas-Groesse in Bildschirm-px (== CSS-px) beim aktuellen Massstab. */
  const [anzeige, setAnzeige] = useState<Flaeche>({ breite: 0, hoehe: 0 });

  // Tool State
  const [tool, setTool] = useState<Tool>('select');
  const [rahmenArt, setRahmenArt] = useState<RahmenArt>('a4');
  const [drehung, setDrehung] = useState<Drehung>(0);

  // Auswahl: waehrend des Ziehens in Bildschirm-px, danach UNSKALIERT
  // gespeichert — so bleibt sie beim Zoomen an ihrer Stelle.
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPos, setStartPos] = useState<Punkt>({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState<Punkt>({ x: 0, y: 0 });
  const [selection, setSelection] = useState<Rahmen | null>(null);

  // Eraser State
  // Store eraser paths as arrays of points relative to the unscaled PDF viewport (1.0 scale)
  // of the CURRENT rotation. Each path is Array<{x, y}>
  const [erasures, setErasures] = useState<Punkt[][]>([]);
  const [isErasing, setIsErasing] = useState(false);
  const [currentErasurePath, setCurrentErasurePath] = useState<Punkt[]>([]);

  // Name: Vorschlag aus Datei + Seite, bis der Nutzer selbst tippt.
  const [eigenerName, setEigenerName] = useState<string | null>(null);
  const [exportiert, setExportiert] = useState(false);
  /** Fehlermeldung des Exports (in der Fussleiste, kein Browser-Dialog). */
  const [exportFehler, setExportFehler] = useState<string | null>(null);

  const basisName = startName ?? planNameAusDatei(file.name);
  const name = eigenerName ?? planNameMitSeite(basisName, pageNum, numPages);
  const nameOk = name.trim().length > 0;
  const verhaeltnis = rahmenArt === 'a4' ? A4_QUER : null;

  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        const arrayBuffer = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setLoading(false);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setLoading(false);
        alert('Fehler beim Laden der PDF. Bitte stellen Sie sicher, dass es sich um eine gültige PDF-Datei handelt.');
        onCancel();
      }
    };
    loadPdf();
  }, [file]);

  useEffect(() => {
    if (!pdfDoc) return;
    renderPage(pageNum);

    // Cleanup function to cancel render if component unmounts or dependencies change
    return () => {
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
        }
    };
  }, [pdfDoc, pageNum, scale, erasures, drehung]); // Re-render if erasures change

  const renderPage = async (num: number) => {
    const lauf = ++renderLaufRef.current;
    // Cancel any existing render task
    if (renderTaskRef.current) {
        try {
            await renderTaskRef.current.cancel();
        } catch {
            // Ignore cancel errors
        }
    }

    const page = await pdfDoc.getPage(num);
    // Ueberholt (Zoom, Blaettern, Drehen waehrend getPage): nicht mehr zeichnen,
    // sonst rendern zwei Laeufe in dasselbe Canvas.
    if (lauf !== renderLaufRef.current) return;
    const grund = page.getViewport({ scale: 1 });
    grundMasseRef.current = { breite: grund.width, hoehe: grund.height };
    const rotation = pdfDrehung(page.rotate, drehung);

    // Einpassen: beim ersten Anzeigen und nach dem Drehen passt die Seite ganz
    // in den sichtbaren Bereich (am Handy ragte ein A3-Plan bei 100 % weit
    // hinaus). Neuer Massstab → der Effekt rendert gleich noch einmal.
    const container = containerRef.current;
    if (einpassenRef.current && container) {
        einpassenRef.current = false;
        const basis = page.getViewport({ scale: 1, rotation });
        const stil = window.getComputedStyle(container);
        const randB = parseFloat(stil.paddingLeft) + parseFloat(stil.paddingRight);
        const randH = parseFloat(stil.paddingTop) + parseFloat(stil.paddingBottom);
        const passend = einpassSkala(basis.width, basis.height, container.clientWidth - randB, container.clientHeight - randH, ZOOM_MIN, 2);
        if (passend !== scale) {
            setScale(passend);
            return;
        }
    }

    const viewport = page.getViewport({ scale, rotation });
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Kein devicePixelRatio: Canvas-Pixel == CSS-Pixel haelt die
    // Overlay-Mathematik 1:1. Scharf wird der Plan beim Uebernehmen (Neurendern).
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    setAnzeige(prev => (prev.breite === canvas.width && prev.hoehe === canvas.height ? prev : { breite: canvas.width, hoehe: canvas.height }));

    const renderContext = {
      canvasContext: context!,
      viewport: viewport,
    };

    // Clear previous render
    context?.clearRect(0, 0, canvas.width, canvas.height);

    try {
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        // After rendering PDF, draw erasures on top
        zeichneRadierungen(context!, erasures, scale);

    } catch (e: any) {
        if (e.name !== 'RenderingCancelledException') {
            console.error("Render error", e);
        }
    }
  };

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent): Punkt => {
      if (!canvasRef.current) return { x: 0, y: 0 };
      const rect = canvasRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
      return {
          x: clientX - rect.left,
          y: clientY - rect.top
      };
  };

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      if (exportiert) return;
      if ('touches' in e && e.touches.length === 0) return;
      const coords = getCoordinates(e);

      if (tool === 'select') {
          setStartPos(coords);
          setCurrentPos(coords);
          setIsSelecting(true);
          setSelection(null);
      } else {
          // Erase Mode
          setIsErasing(true);
          // Store point in unscaled coordinates
          const point = { x: coords.x / scale, y: coords.y / scale };
          setCurrentErasurePath([point]);

          // Draw immediate feedback (dot)
          const ctx = canvasRef.current?.getContext('2d');
          if (ctx) {
              ctx.beginPath();
              ctx.fillStyle = '#ffffff';
              ctx.arc(coords.x, coords.y, (RADIERER_BREITE * scale) / 2, 0, Math.PI * 2);
              ctx.fill();
          }
      }
  };

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      if ('touches' in e && e.touches.length === 0) return;
      const coords = getCoordinates(e);

      if (tool === 'select') {
          if (!isSelecting) return;
          setCurrentPos(coords);
      } else {
          // Erase Mode
          if (!isErasing) return;

          const point = { x: coords.x / scale, y: coords.y / scale };
          const newPath = [...currentErasurePath, point];
          setCurrentErasurePath(newPath);

          // Draw immediate line segment
          const ctx = canvasRef.current?.getContext('2d');
          if (ctx && currentErasurePath.length > 0) {
              const prev = currentErasurePath[currentErasurePath.length - 1];
              ctx.beginPath();
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = RADIERER_BREITE * scale;
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.moveTo(prev.x * scale, prev.y * scale);
              ctx.lineTo(point.x * scale, point.y * scale);
              ctx.stroke();
          }
      }
  };

  const handleMouseUp = () => {
      if (tool === 'select') {
          if (!isSelecting) return;
          setIsSelecting(false);

          // Bei „A4 quer“ behaelt der Rahmen √2, immer innerhalb der Seite.
          const r = rahmenMitVerhaeltnis(startPos, currentPos, verhaeltnis, anzeige);
          if (r.w > 10 && r.h > 10) {
              setSelection({ x: r.x / scale, y: r.y / scale, w: r.w / scale, h: r.h / scale });
          }
      } else {
          // Erase Mode
          if (!isErasing) return;
          setIsErasing(false);
          if (currentErasurePath.length > 0) {
              setErasures(prev => [...prev, currentErasurePath]);
          }
          setCurrentErasurePath([]);
      }
  };

  /** Blaettern: Auswahl und Radierungen gehoeren zur Seite und fallen weg. */
  const seiteWechseln = (neu: number) => {
      if (neu === pageNum || neu < 1 || neu > numPages) return;
      setPageNum(neu);
      setSelection(null);
      setErasures([]);
      setIsSelecting(false);
  };

  /**
   * 90° im Uhrzeigersinn. Radierungen werden mitgedreht (sie brauchen die
   * Hoehe VOR der Drehung, unskaliert); die Auswahl faellt weg — ein A4-quer-
   * Rahmen waere gedreht hochkant.
   */
  const drehen = () => {
      if (loading || exportiert) return;
      const grund = grundMasseRef.current;
      if (grund) {
          const hoeheVorher = drehung % 180 === 0 ? grund.hoehe : grund.breite;
          setErasures(prev => radierungenDrehen90(prev, hoeheVorher));
      } else {
          setErasures([]);
      }
      setSelection(null);
      setIsSelecting(false);
      einpassenRef.current = true;
      setDrehung(d => naechsteDrehung(d));
  };

  const rahmenArtWaehlen = (art: RahmenArt) => {
      setRahmenArt(art);
      setTool('select');
      if (art !== rahmenArt) setSelection(null);
  };

  /**
   * Scharf uebernehmen: die Seite wird fuer den gewaehlten Bereich neu
   * gerendert (lange Kante ≈ 4000 px, Deckel 4096) — frueher wurde das
   * Bildschirm-Canvas (≈ 72 dpi) abgegriffen und die Schrift im Bericht war
   * unscharf. Radierungen werden im Export-Massstab nachgezeichnet.
   */
  const performCrop = async () => {
      if (!pdfDoc || exportiert || !nameOk) return;
      setExportiert(true);
      setExportFehler(null);
      const exportCanvas = document.createElement('canvas');
      try {
          const page = await pdfDoc.getPage(pageNum);
          const rotation = pdfDrehung(page.rotate, drehung);
          const basis = page.getViewport({ scale: 1, rotation });
          // Bereich in unskalierten Viewport-Einheiten, in die Seite geklemmt.
          const roh = selection ?? { x: 0, y: 0, w: basis.width, h: basis.height };
          const x = Math.max(0, Math.min(roh.x, basis.width));
          const y = Math.max(0, Math.min(roh.y, basis.height));
          const w = Math.max(1, Math.min(roh.w, basis.width - x));
          const h = Math.max(1, Math.min(roh.h, basis.height - y));

          const s = exportSkala(w, h);
          exportCanvas.width = Math.max(1, Math.round(w * s));
          exportCanvas.height = Math.max(1, Math.round(h * s));
          const ctx = exportCanvas.getContext('2d');
          if (!ctx) throw new Error('Kein Canvas-Kontext');
          // JPEG kennt keine Transparenz: weisser Grund statt schwarzer Flaechen.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

          // Nur den Bereich rendern: der Viewport wird um den Bereichsursprung verschoben.
          const viewport = page.getViewport({ scale: s, rotation, offsetX: -x * s, offsetY: -y * s });
          await page.render({ canvasContext: ctx, viewport }).promise;

          ctx.setTransform(s, 0, 0, s, -x * s, -y * s);
          zeichneRadierungen(ctx, erasures, 1);
          ctx.setTransform(1, 0, 0, 1, 0, 0);

          const dataUrl = exportCanvas.toDataURL('image/jpeg', EXPORT_QUALITAET);
          if (!dataUrl.startsWith('data:image/jpeg')) throw new Error('Export leer');
          onCrop(dataUrl, name.trim());
      } catch (e) {
          console.error('Plan-Export fehlgeschlagen', e);
          setExportFehler('Der Plan konnte nicht in hoher Auflösung aufbereitet werden. Bitte einen kleineren Ausschnitt wählen oder erneut versuchen.');
      } finally {
          // Speicher sofort freigeben (grosse Canvas halten am Handy sonst Speicher fest).
          exportCanvas.width = 0;
          exportCanvas.height = 0;
          setExportiert(false);
      }
  };

  const clearErasures = () => {
      setErasures([]);
      // Re-render handled by effect dependency
  };

  const rahmenAnzeige: Rahmen | null = isSelecting
      ? rahmenMitVerhaeltnis(startPos, currentPos, verhaeltnis, anzeige)
      : selection
          ? { x: selection.x * scale, y: selection.y * scale, w: selection.w * scale, h: selection.h * scale }
          : null;
  const hochformat = anzeige.hoehe > anzeige.breite;
  const werkzeugKnopf = (aktiv: boolean) =>
      `flex items-center gap-2 px-3 min-h-[40px] rounded-md transition-all ${aktiv ? 'bg-blue-600 text-white shadow-md font-bold' : 'text-slate-300 hover:bg-slate-600'}`;

  return (
    <div className="fixed inset-0 z-[140] bg-slate-900/95 flex flex-col animate-fade-in text-white">
      {/* Werkzeugleiste: nur Werkzeuge, darf scrollen. Name, Uebernehmen und
          Abbrechen stehen unten in der festen Fussleiste (am Handy sonst erst
          nach langem Wischen erreichbar). */}
      <div className="bg-slate-800 p-3 md:p-4 flex items-center shrink-0 shadow-md z-10 overflow-x-auto">
         <div className="flex items-center gap-3 md:gap-4">
             <div className="hidden md:block">
                <h3 className="font-bold text-lg">Plan anlegen</h3>
                <p className="text-xs text-slate-400">Radiere Unnötiges weg und wähle den Bereich.</p>
             </div>

             {/* Tool Switcher */}
             <div className="flex bg-slate-700 rounded-lg p-1 gap-1 shrink-0">
                 <button
                    type="button"
                    onClick={() => setTool('select')}
                    className={werkzeugKnopf(tool === 'select')}
                    title="Bereich wählen"
                 >
                     <MousePointer className="w-4 h-4"/> <span className="hidden sm:inline">Auswahl</span>
                 </button>
                 <button
                    type="button"
                    onClick={() => setTool('erase')}
                    className={werkzeugKnopf(tool === 'erase')}
                    title="Radierer"
                 >
                     <Eraser className="w-4 h-4"/> <span className="hidden sm:inline">Radierer</span>
                 </button>
             </div>

             {/* Rahmenformat: A4 quer fuellt die Berichtsseite genau. */}
             <div className="flex bg-slate-700 rounded-lg p-1 gap-1 shrink-0" role="group" aria-label="Rahmenformat">
                 <button
                    type="button"
                    onClick={() => rahmenArtWaehlen('a4')}
                    className={werkzeugKnopf(rahmenArt === 'a4')}
                    aria-pressed={rahmenArt === 'a4'}
                    title="Rahmen im Format A4 quer (füllt die Berichtsseite)"
                 >
                     <RectangleHorizontal className="w-4 h-4"/> <span className="whitespace-nowrap">A4 quer</span>
                 </button>
                 <button
                    type="button"
                    onClick={() => rahmenArtWaehlen('frei')}
                    className={werkzeugKnopf(rahmenArt === 'frei')}
                    aria-pressed={rahmenArt === 'frei'}
                    title="Rahmen frei ziehen"
                 >
                     <SquareDashed className="w-4 h-4"/> <span>Frei</span>
                 </button>
             </div>

             <div className="flex bg-slate-700 rounded-lg p-1 gap-1 shrink-0">
                 <button
                    type="button"
                    onClick={drehen}
                    disabled={loading || exportiert}
                    className="flex items-center gap-2 px-3 min-h-[40px] rounded-md text-slate-200 hover:bg-slate-600 disabled:opacity-40"
                    title="90° drehen (Hochformat-Plan quer legen)"
                    aria-label="90° drehen"
                 >
                     <RotateCw className="w-4 h-4"/> <span className="hidden sm:inline">Drehen</span>
                 </button>
                 <button
                    type="button"
                    onClick={() => { setSelection(null); setIsSelecting(false); }}
                    disabled={!selection}
                    className="flex items-center gap-2 px-3 min-h-[40px] rounded-md text-slate-200 hover:bg-slate-600 disabled:opacity-40"
                    title="Auswahl aufheben — ganze Seite übernehmen"
                    aria-label="Ganze Seite"
                 >
                     <Maximize className="w-4 h-4"/> <span className="hidden sm:inline whitespace-nowrap">Ganze Seite</span>
                 </button>
             </div>

             {/* Pagination */}
             {numPages > 1 && (
                 <div className="flex items-center bg-slate-700 rounded-lg p-1 shrink-0">
                     <button type="button" onClick={() => seiteWechseln(pageNum - 1)} disabled={pageNum <= 1} className="p-2 min-h-[40px] hover:bg-slate-600 rounded disabled:opacity-30" aria-label="Vorige Seite"><ChevronLeft className="w-5 h-5"/></button>
                     <span className="px-3 text-sm font-mono whitespace-nowrap">{pageNum} / {numPages}</span>
                     <button type="button" onClick={() => seiteWechseln(pageNum + 1)} disabled={pageNum >= numPages} className="p-2 min-h-[40px] hover:bg-slate-600 rounded disabled:opacity-30" aria-label="Nächste Seite"><ChevronRight className="w-5 h-5"/></button>
                 </div>
             )}

             {/* Zoom */}
             <div className="flex bg-slate-700 rounded-lg p-1 shrink-0">
                 <button type="button" onClick={() => setScale(s => Math.max(ZOOM_MIN, s - ZOOM_SCHRITT))} className="p-2 min-h-[40px] hover:bg-slate-600 rounded" aria-label="Verkleinern"><ZoomOut className="w-5 h-5"/></button>
                 <span className="p-2 text-xs font-mono w-14 text-center self-center">{Math.round(scale * 100)}%</span>
                 <button type="button" onClick={() => setScale(s => Math.min(ZOOM_MAX, s + ZOOM_SCHRITT))} className="p-2 min-h-[40px] hover:bg-slate-600 rounded" aria-label="Vergrößern"><ZoomIn className="w-5 h-5"/></button>
             </div>
         </div>
      </div>

      {/* Main Area — `m-auto` am Inhalt statt justify-center: so bleibt ein
          vergroesserter Plan nach links/oben scrollbar. */}
      <div className="relative flex-1 min-h-0">
        <div className={`absolute inset-0 overflow-auto flex p-4 md:p-8 bg-slate-900 select-none ${tool === 'erase' ? 'cursor-cell' : 'cursor-crosshair'}`} ref={containerRef}>
           {loading ? (
               <div className="m-auto flex flex-col items-center text-slate-400">
                   <Loader2 className="w-10 h-10 animate-spin mb-4" />
                   <p>PDF wird geladen...</p>
               </div>
           ) : (
               <div
                  className="relative shadow-2xl m-auto shrink-0"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleMouseDown}
                  onTouchMove={handleMouseMove}
                  onTouchEnd={handleMouseUp}
                  // React haengt touchmove passiv an, das preventDefault oben greift
                  // nicht: ohne touch-action scrollt der Container beim Radieren
                  // und Auswaehlen mit (dieselbe Falle wie im ImageEditor).
                  style={{ touchAction: 'none' }}
               >
                   <canvas ref={canvasRef} className="block bg-white" />

                   {/* Selection Box Overlay */}
                   {rahmenAnzeige && (
                       <div
                          className="absolute border-2 border-blue-500 bg-blue-500/20 pointer-events-none"
                          style={{ left: rahmenAnzeige.x, top: rahmenAnzeige.y, width: rahmenAnzeige.w, height: rahmenAnzeige.h }}
                       >
                          <div className="absolute top-0 left-0 bg-blue-600 text-white text-[11px] px-1 font-bold">
                              {rahmenArt === 'a4' ? 'A4 quer' : 'Ausschnitt'}
                          </div>
                       </div>
                   )}

                   {!isSelecting && !selection && tool === 'select' && (
                       <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-2">
                           <div className="bg-black/50 text-white px-4 py-2 rounded-lg backdrop-blur-sm flex flex-col items-center gap-1 text-center">
                               <span className="flex items-center gap-2">
                                   <MousePointer2 className="w-4 h-4 shrink-0" />
                                   <span className="text-sm font-bold">
                                       {rahmenArt === 'a4' ? 'Rahmen ziehen – Format A4 quer' : 'Bereich ziehen zum Ausschneiden'}
                                   </span>
                               </span>
                               {rahmenArt === 'a4' && hochformat && (
                                   <span className="text-xs text-slate-200">Hochformat-Plan? Mit „Drehen“ quer legen.</span>
                               )}
                           </div>
                       </div>
                   )}
               </div>
           )}
        </div>

        {/* Eraser Hint */}
        {tool === 'erase' && erasures.length > 0 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3 animate-slide-up z-50 whitespace-nowrap">
                <span className="text-xs">Radierungen: {erasures.length}</span>
                <button type="button" onClick={clearErasures} className="text-red-400 hover:text-red-300 text-xs font-bold uppercase min-h-[32px]">Alle löschen</button>
            </div>
        )}

        {exportiert && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/60" role="status">
                <div className="flex items-center gap-3 rounded-xl bg-slate-800 px-5 py-3 shadow-xl">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm font-semibold">Plan wird in hoher Auflösung aufbereitet …</span>
                </div>
            </div>
        )}
      </div>

      {/* Fussleiste: immer sichtbar, Daumenweite am Handy; rechtsbuendig am Desktop. */}
      <div className="bg-slate-800 border-t border-slate-700 p-3 md:p-4 shrink-0 pb-safe">
          {exportFehler && (
              <p role="alert" className="mb-3 rounded-lg bg-red-900/60 border border-red-700 px-3 py-2 text-sm text-red-100">{exportFehler}</p>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="block flex-1 min-w-0">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Planname</span>
                  <input
                      type="text"
                      value={name}
                      onChange={(e) => setEigenerName(e.target.value)}
                      onFocus={(e) => e.currentTarget.select()}
                      placeholder="z. B. Grundriss EG"
                      maxLength={120}
                      enterKeyHint="done"
                      aria-invalid={!nameOk}
                      className={`w-full h-11 rounded-lg border bg-slate-900 px-3 text-sm text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500 ${nameOk ? 'border-slate-600' : 'border-amber-400'}`}
                  />
                  {!nameOk && <span className="mt-1 block text-[11px] text-amber-300">Bitte einen Namen für den Plan eingeben.</span>}
              </label>
              <div className="grid grid-cols-2 gap-3 sm:flex sm:shrink-0">
                  <Button variant="ghost" onClick={onCancel} disabled={exportiert} className="text-white hover:bg-slate-700 w-full sm:w-auto justify-center min-h-[44px]">Abbrechen</Button>
                  <Button
                     variant="primary"
                     onClick={performCrop}
                     loading={exportiert}
                     disabled={!nameOk || loading || !pdfDoc}
                     icon={selection ? <Crop className="w-4 h-4"/> : <Check className="w-4 h-4"/>}
                     className="w-full sm:w-auto justify-center min-h-[44px]"
                  >
                     {selection ? 'Ausschnitt übernehmen' : 'Ganze Seite übernehmen'}
                  </Button>
              </div>
          </div>
      </div>
    </div>
  );
};
